import { createContext, useContext, useState, ReactNode } from 'react';
import * as React from 'react';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ChatContextType {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  clearMessages: () => void;
  suggestedToolIds: string[];
  newToolIds: Set<string>;
  addSuggestedTool: (toolId: string) => void;
  markNewBatch: () => void;
  clearSuggestedTools: () => void;
  pendingInput: string | null;
  setPendingInput: React.Dispatch<React.SetStateAction<string | null>>;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  // Initialize with empty state - no persistence across refreshes
  const [messages, setMessages] = useState<Message[]>([]);
  const [suggestedToolIds, setSuggestedToolIds] = useState<string[]>([]);
  const [newToolIds, setNewToolIds] = useState<Set<string>>(new Set());
  const [pendingInput, setPendingInput] = useState<string | null>(null);

  const clearMessages = () => {
    setMessages([]);
    clearSuggestedTools();
  };

  const markNewBatch = () => {
    // Called before a new assistant message starts — clears the "new" markers
    setNewToolIds(new Set());
  };

  const addSuggestedTool = (toolId: string) => {
    setSuggestedToolIds(prev => {
      // Remove if already exists (will re-add at top)
      const without = prev.filter(id => id !== toolId);
      // Prepend new tool, cap at 8
      return [toolId, ...without].slice(0, 8);
    });
    setNewToolIds(prev => new Set(prev).add(toolId));
  };

  const clearSuggestedTools = () => {
    setSuggestedToolIds([]);
    setNewToolIds(new Set());
  };

  return (
    <ChatContext.Provider value={{
      messages,
      setMessages,
      clearMessages,
      suggestedToolIds,
      newToolIds,
      addSuggestedTool,
      markNewBatch,
      clearSuggestedTools,
      pendingInput,
      setPendingInput,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat(): ChatContextType {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider.');
  }
  return context;
}
